import { useCallback, useEffect, useRef, useState } from 'react';
import { Plus, Search, Truck, Users, Cpu, Building2, Eye, Edit, Video, ClipboardList, Receipt as ReceiptIcon, Send, Cable } from 'lucide-react';
import { Input } from '../../components/ui/Input';
import { Button } from '../../components/ui/Button';
import { Card } from '../../components/ui/Card';
import { useVehicles } from '../../hooks/useVehicles';
import { useDrivers } from '../../hooks/useDrivers';
import { useUsers, type AdminUser } from '../../hooks/useUsers';
import { useAuth } from '../../context/AuthContext';
import {
  getSubunits, createUser, updateUser, resendUserInvitation, createDriver, createVehicle, updateVehicle,
  getDriverAssignments, createDriverAssignment, closeDriverAssignment,
  createSubunit, updateSubunit, getAllDevices,
  type UserCreateDto, type UserUpdateDto, type VehicleCreateDto, type VehicleUpdateDto, type DeviceDto, type DriverCreateDto,
} from '../../lib/api/endpoints';
import { ApiError } from '../../lib/api/client';
import { VehicleDetailModal } from '../../components/vehicles/VehicleDetailModal';
import { AdminFormModal } from '../../components/admin/AdminFormModal';
import type { AdminEditableItem, AdminFormPayload, ResultadoInvitacion } from '../../components/admin/AdminFormModal';
import { DeviceCamerasModal } from '../../components/admin/DeviceCamerasModal';
import { DeviceInstallModal } from '../../components/admin/DeviceInstallModal';
import { LogbookTypesPanel } from '../../components/admin/LogbookTypesPanel';
import { ExpenseCategoriesPanel } from '../../components/admin/ExpenseCategoriesPanel';
import type { Vehicle } from '../../types/vehicle';
import clsx from 'clsx';

function describeApiError(err: unknown): string {
  if (err instanceof ApiError) return err.detail ? `${err.title}: ${err.detail}` : err.title;
  return err instanceof Error ? err.message : 'error desconocido';
}

type AdminTab = 'users' | 'vehicles' | 'devices' | 'subunits' | 'logbook-types' | 'expense-categories';

interface AdminSubunit {
  id: string;
  name: string;
  vehicles: number;
  drivers: number;
}

const TABS: { id: AdminTab; label: string; icon: React.ReactNode }[] = [
  { id: 'users', label: 'Usuarios', icon: <Users size={16} /> },
  { id: 'vehicles', label: 'Vehículos', icon: <Truck size={16} /> },
  { id: 'devices', label: 'Dispositivos', icon: <Cpu size={16} /> },
  { id: 'subunits', label: 'Sub-unidades', icon: <Building2 size={16} /> },
  // FE-13 — la taxonomía de novedades es del TENANT y solo el Administrador la
  // toca (ADR-015 §41): un Supervisor que pudiera inventar tipos partiría en
  // dos los reportes de toda la empresa, porque el rollup agrupa por `code`.
  { id: 'logbook-types', label: 'Tipos de novedad', icon: <ClipboardList size={16} /> },
  // FE-12 — mismo criterio para la taxonomía de gastos (ADR-015): es del
  // TENANT entero y solo el Administrador la toca, porque el rollup de
  // reportes agrupa por `code`.
  { id: 'expense-categories', label: 'Categorías de gasto', icon: <ReceiptIcon size={16} /> },
];

const ROLE_BADGE_COLORS: Record<string, string> = {
  Supervisor: '#3B82F6', Ejecutivo: '#8B5CF6',
  Administrador: '#F59E0B', Operador: '#10B981',
};

export default function AdminPage() {
  const { user } = useAuth();
  // Issue #77: un Supervisor entra a esta misma ruta (nav/router ya lo
  // permiten), pero PRODUCT.md §4.8 solo le da "crear/eliminar Operador, su
  // sub-unidad" — nada de vehículos, dispositivos ni sub-unidades, que son
  // administración de la flota/organización (Administrador). Se filtran las
  // pestañas en vez de bloquear la ruta entera.
  const isSupervisor = user?.role === 'supervisor';
  const visibleTabs = isSupervisor ? TABS.filter(t => t.id === 'users') : TABS;

  const [activeTab, setActiveTab] = useState<AdminTab>('users');
  const [search, setSearch] = useState('');

  // Revisión de paneles 2026-08-31 (PENDIENTES.md/FE-04): las 4 pestañas
  // mostraban listas hardcodeadas (`INITIAL_USERS`/`INITIAL_SUBUNITS`, y
  // vehículos leía mockData.ts) mientras GET /users, /subunits y /vehicles
  // ya estaban implementados. Usuarios, Vehículos y Sub-unidades quedaron
  // conectados a POST/PATCH reales en dos bugfix sucesivos (2026-09-01).
  // Dispositivos se conectó en FE-14: `GET /devices` real (id/imei/vehículo
  // instalado) en vez de reusar `vehicles` con `deviceId` siempre vacío
  // (issue backend #54, sin resolver). No hay alta/edición de dispositivo
  // en sí (`openapi.yaml` sí tiene `POST /devices`, pero no es el objetivo
  // de FE-14) — solo listar y declarar sus cámaras.
  const { vehicles, refetch: refetchVehicles } = useVehicles();

  const [devicesList, setDevicesList] = useState<DeviceDto[]>([]);
  const [devicesLoading, setDevicesLoading] = useState(true);
  const refetchDevices = useCallback(async () => {
    setDevicesLoading(true);
    try {
      setDevicesList(await getAllDevices());
    } catch {
      // Sin sesión real (loginDev, FE-01) el cliente lanza antes de llegar a
      // la red — mismo criterio que useVehicles.ts/useUsers.ts: lista vacía,
      // no una pantalla rota.
      setDevicesList([]);
    } finally {
      setDevicesLoading(false);
    }
  }, []);
  useEffect(() => { refetchDevices(); }, [refetchDevices]);

  const [camerasDevice, setCamerasDevice] = useState<DeviceDto | null>(null);
  const [installDevice, setInstallDevice] = useState<DeviceDto | null>(null);

  const { users: usersList, refetch: refetchUsers } = useUsers();

  // Opciones reales para el formulario de usuarios/vehículos (independiente
  // del conteo de vehículos/conductores por sub-unidad que arma la pestaña
  // Sub-unidades más abajo).
  const [subunitOptions, setSubunitOptions] = useState<{ id: string; name: string }[]>([]);
  const refetchSubunitOptions = useCallback(async () => {
    const res = await getSubunits();
    setSubunitOptions(res.items.map(s => ({ id: s.id, name: s.name })));
  }, []);
  // Sin `catch`, un fallo de `/subunits` (403, 500, red) era una promesa
  // rechazada sin manejar y el selector de sub-unidad quedaba vacío sin
  // decir por qué. Vaciar la lista es lo que ya pasaba; lo que cambia es que
  // deja de ser un error sin capturar.
  useEffect(() => { refetchSubunitOptions().catch(() => setSubunitOptions([])); }, [refetchSubunitOptions]);

  // Subunit.{vehicles,drivers} no existen en el schema `Subunit` real (solo
  // trae `{id, name}`) — se cuentan contra los vehículos/conductores ya
  // cargados, igual que se resuelve `subUnitName` en otras pantallas.
  const { drivers: fetchedDrivers } = useDrivers();
  // Solo conductores sin cuenta de Operador ya vinculada — evita ofrecer un
  // conductor que ya tiene login y terminaría con dos cuentas (form Usuarios).
  // `subUnitId` viaja para que AdminFormModal derive ahí la sub-unidad del
  // Operador (issue #77): es la del conductor, no una que se elija aparte.
  // Cualquier conductor se puede asignar a un vehículo (form Vehículos).
  const allDriverOptions = fetchedDrivers.map(d => ({ id: d.id, name: d.name }));
  // Derivado, no estado propio — así los conteos se recalculan solos cuando
  // `vehicles`/`fetchedDrivers` cambian (p.ej. al reasignar un vehículo de
  // sub-unidad), sin depender de un refetch manual que se olvide de llamar.
  const subunitsList: AdminSubunit[] = subunitOptions.map(s => ({
    id: s.id,
    name: s.name,
    vehicles: vehicles.filter(v => v.subUnitId === s.id).length,
    drivers: fetchedDrivers.filter(d => d.subUnitId === s.id).length,
  }));

  const [selectedVehicle, setSelectedVehicle] = useState<Vehicle | null>(null);
  
  // Modal states for Add / Edit
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [editingItem, setEditingItem] = useState<AdminEditableItem | null>(null);

  useEffect(() => { document.title = 'Administración | Paitron Radar'; }, []);

  // Issue #196: en el alta de un Operador creamos primero el conductor
  // (POST /drivers) y luego la cuenta (POST /users). Si la cuenta falla
  // (p. ej. correo repetido, 409), el conductor ya quedó creado; guardamos su
  // id aquí para que un reintento reutilice ese conductor en vez de crear un
  // duplicado. Se limpia al abrir un alta nueva y al lograr la creación.
  const pendingDriverIdRef = useRef<string | null>(null);

  const handleOpenAdd = () => {
    pendingDriverIdRef.current = null;
    setEditingItem(null);
    setIsFormOpen(true);
  };

  // Reenvío de la invitación. El caso normal, no la excepción: el enlace
  // vence en horas, se gasta al usarlo y el correo se pierde. Sin esto la
  // única salida sería dar de baja al usuario y recrearlo.
  const [reenviando, setReenviando] = useState<string | null>(null);
  const [avisoInvitacion, setAvisoInvitacion] = useState<string | null>(null);

  const handleReenviarInvitacion = async (u: AdminUser, e: React.MouseEvent) => {
    e.stopPropagation();
    setAvisoInvitacion(null);
    setReenviando(u.id);
    try {
      await resendUserInvitation(u.id);
      setAvisoInvitacion(`Invitación reenviada a ${u.email}. El enlace anterior deja de servir.`);
    } catch (err) {
      setAvisoInvitacion(
        err instanceof ApiError && err.status === 409
          ? `${u.email} está dado de baja: reactívalo antes de invitarlo.`
          : `No se pudo reenviar la invitación a ${u.email}.`,
      );
    } finally {
      setReenviando(null);
    }
  };

  const handleOpenEdit = (item: AdminEditableItem, e?: React.MouseEvent) => {
    if (e) e.stopPropagation();
    setEditingItem(item);
    setIsFormOpen(true);
  };

  // Deja que el error (ApiError o de red) se propague: AdminFormModal lo
  // atrapa y lo muestra sin cerrar el modal.
  const handleSaveUser = async (data: UserCreateDto | UserUpdateDto): Promise<{ invitation?: ResultadoInvitacion; warning?: string } | void> => {
    if (editingItem) {
      const updatePayload = data as UserUpdateDto;
      const updated = await updateUser(editingItem.id, updatePayload);
      await refetchUsers();
      // El core no puede vaciar `subunitIds` por PATCH todavía (limitación
      // conocida, EP-17-crud-gestion.md §7 "Un PATCH no puede vaciar un
      // campo opcional") — verificado el 2026-09-01: pedir subunitIds: []
      // para desvincular un Supervisor lo deja igual. El resto de campos
      // (rol, nombre, estado) sí se guarda; se avisa en vez de fingir éxito
      // total.
      if ((updatePayload.subunitIds?.length ?? 0) === 0 && (updated.subunitIds?.length ?? 0) > 0) {
        return {
          warning: 'El rol, nombre y estado se guardaron. La sub-unidad NO se pudo quitar: '
            + 'es una limitación conocida del backend (todavía no puede vaciar ese campo por '
            + 'PATCH). Si necesitas quitarla de verdad, hay que resolverlo del lado del backend.',
        };
      }
      return;
    }
    // Alta de OPERADOR (issue #196): el modal manda los datos del conductor
    // en `newDriver`. Creamos el conductor, tomamos su id y creamos la cuenta
    // con él. El ref evita duplicar el conductor si la cuenta falla y el
    // usuario reintenta (ver pendingDriverIdRef).
    const createData = data as UserCreateDto & { newDriver?: DriverCreateDto };
    const { newDriver, ...userFields } = createData;
    let driverId = userFields.driverId;
    if (newDriver) {
      if (!pendingDriverIdRef.current) {
        const driver = await createDriver(newDriver);
        pendingDriverIdRef.current = driver.id;
      }
      driverId = pendingDriverIdRef.current;
    }
    const created = await createUser({ ...userFields, driverId });
    pendingDriverIdRef.current = null;
    await refetchUsers();
    return {
      invitation: {
        status: created.invitation.status,
        email: created.email,
        detail: created.invitation.detail,
      },
    };
  };

  // La reasignación de conductor es un recurso aparte con vigencia temporal
  // (`/driver-assignments`, openapi.yaml) — no un campo del vehículo. Cerrar
  // la asignación vigente es lo que libera el vehículo para la siguiente
  // (EXCLUDE de vigencias, ADR-007); si eso falla tras guardar el vehículo,
  // se avisa en vez de fingir un éxito total (mismo patrón que usuarios).
  const handleSaveVehicle = async (
    data: (VehicleCreateDto | VehicleUpdateDto) & { driverId?: string },
  ): Promise<{ warning?: string } | void> => {
    const { driverId: nextDriverId, ...vehicleFields } = data;

    let vehicleId: string;
    let currentDriverId: string | null;
    if (editingItem) {
      const updated = await updateVehicle(editingItem.id, vehicleFields as VehicleUpdateDto);
      vehicleId = updated.id;
      currentDriverId = (editingItem as Vehicle).driverId ?? null;
    } else {
      const created = await createVehicle(vehicleFields as VehicleCreateDto);
      vehicleId = created.id;
      currentDriverId = null;
    }
    await refetchVehicles();

    const wantsDriverId: string | null = nextDriverId || null;
    if (wantsDriverId === currentDriverId) return;

    try {
      const nowIso = new Date().toISOString();
      if (currentDriverId) {
        const activePage = await getDriverAssignments({ vehicleId, activeAt: nowIso });
        const active = activePage.items[0];
        if (active) await closeDriverAssignment(active.id, nowIso);
      }
      if (wantsDriverId) {
        await createDriverAssignment({ driverId: wantsDriverId, vehicleId, validFrom: nowIso });
      }
      await refetchVehicles();
    } catch (err) {
      return { warning: `El vehículo se guardó. La reasignación de conductor falló: ${describeApiError(err)}.` };
    }
  };

  const handleSaveSubunit = async (data: { name: string }) => {
    if (editingItem) {
      await updateSubunit(editingItem.id, { name: data.name });
    } else {
      await createSubunit({ name: data.name });
    }
    // `subunitsList` es derivado de `subunitOptions` — un solo refetch basta.
    await refetchSubunitOptions();
  };

  // El modal arma un payload distinto por pestana y `AdminFormPayload` no
  // sabe cual: el estrechamiento lo decide `activeTab`, que es justo lo que
  // TypeScript no puede inferir desde aqui.
  const handleSaveItem = (data: AdminFormPayload) => {
    if (activeTab === 'users') {
      return handleSaveUser(data as UserCreateDto | UserUpdateDto);
    } else if (activeTab === 'vehicles') {
      return handleSaveVehicle(data as (VehicleCreateDto | VehicleUpdateDto) & { driverId?: string });
    } else if (activeTab === 'subunits') {
      return handleSaveSubunit(data as { name: string });
    }
  };

  return (
    <div className="admin-page">
      {/* Tabs — un Supervisor solo tiene 'users' disponible (visibleTabs),
          así que no tiene sentido mostrar un selector de una sola opción. */}
      {visibleTabs.length > 1 && (
        <div className="admin-page__tabs">
          {visibleTabs.map(tab => (
            <button
              key={tab.id}
              id={`admin-tab-${tab.id}`}
              className={clsx('admin-page__tab', activeTab === tab.id && 'admin-page__tab--active')}
              onClick={() => { setActiveTab(tab.id); setSearch(''); }}
            >
              {tab.icon}
              {tab.label}
            </button>
          ))}
        </div>
      )}

      {/* Content */}
      <div className="admin-page__content">
        {/* Toolbar */}
        <div className="admin-page__toolbar">
          <Input
            id="admin-search"
            placeholder={`Buscar ${TABS.find(t => t.id === activeTab)?.label.toLowerCase()}...`}
            value={search}
            onChange={e => setSearch(e.target.value)}
            leftIcon={<Search size={15} />}
          />
          {/* No hay alta de dispositivo en el alcance de FE-14 (solo declarar
              cámaras de uno ya activado) — el formulario mock se retiró. */}
          {activeTab !== 'devices' && activeTab !== 'logbook-types' && activeTab !== 'expense-categories' && (
            <Button
              variant="primary"
              size="sm"
              leftIcon={<Plus size={15} />}
              id="admin-add-btn"
              onClick={handleOpenAdd}
            >
              Agregar
            </Button>
          )}
        </div>

        {/* Users tab */}
        {activeTab === 'users' && (
          <div className="admin-table-container">
            {avisoInvitacion && (
              <div
                role="status"
                style={{
                  padding: '0.75rem 1rem', marginBottom: '0.75rem',
                  background: 'rgba(59,130,246,0.12)', border: '1px solid rgba(59,130,246,0.35)',
                  borderRadius: 'var(--radius-md)', color: 'var(--text-primary)',
                  fontSize: '13px', lineHeight: 1.5,
                }}
              >
                {avisoInvitacion}
              </div>
            )}
            <div className="admin-table" style={{ minWidth: '820px', gridTemplateColumns: '1fr 1.2fr 1fr 1fr 80px 180px' }}>
              <div className="admin-table__header" style={{ gridTemplateColumns: '1fr 1.2fr 1fr 1fr 80px 180px' }}>
                <span>Nombre</span><span>Email</span><span>Rol</span><span>Unidad</span><span>Estado</span><span>Acción</span>
              </div>
              {usersList.filter(u =>
                u.name.toLowerCase().includes(search.toLowerCase()) ||
                u.email.toLowerCase().includes(search.toLowerCase())
              ).map(u => (
                <div key={u.id} className="admin-table__row" id={`admin-user-${u.id}`} style={{ gridTemplateColumns: '1fr 1.2fr 1fr 1fr 80px 180px' }}>
                  <span className="admin-table__name">{u.name}</span>
                  <span className="admin-table__email">{u.email}</span>
                  <span>
                    <span className="admin-table__role-badge"
                      style={{ color: ROLE_BADGE_COLORS[u.role], background: `${ROLE_BADGE_COLORS[u.role]}18` }}>
                      {u.role}
                    </span>
                  </span>
                  <span className="admin-table__unit">{u.unit}</span>
                  <span>
                    <span className={clsx('admin-table__status', u.active ? 'admin-table__status--active' : 'admin-table__status--inactive')}>
                      {u.active ? 'Activo' : 'Inactivo'}
                    </span>
                  </span>
                  <span className="admin-table__actions">
                    {/* Un Supervisor solo administra Operadores (puedeAdministrarUsuario,
                        usuarios.go:349) — mostrar "Editar" en otra fila terminaría en un
                        403 al guardar, incluida su propia fila. */}
                    {(!isSupervisor || u.roleValue === 'OPERADOR') && (
                      <>
                        <Button variant="ghost" size="sm" leftIcon={<Edit size={13} />} onClick={(e) => handleOpenEdit(u, e)}>
                          Editar
                        </Button>
                        {/* Solo para cuentas activas: el backend responde 409
                            sobre una dada de baja, y ofrecerlo sería prometer
                            algo que va a fallar. */}
                        {u.active && (
                          <Button
                            variant="ghost"
                            size="sm"
                            leftIcon={<Send size={13} />}
                            isLoading={reenviando === u.id}
                            onClick={(e) => void handleReenviarInvitacion(u, e)}
                          >
                            Reenviar invitación
                          </Button>
                        )}
                      </>
                    )}
                  </span>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Vehicles tab */}
        {activeTab === 'vehicles' && (
          <div className="admin-table-container">
            <div className="admin-table" style={{ minWidth: '850px', gridTemplateColumns: '1fr 1.2fr 1.2fr 80px 1fr 1fr 140px' }}>
              <div className="admin-table__header" style={{ gridTemplateColumns: '1fr 1.2fr 1.2fr 80px 1fr 1fr 140px' }}>
                <span>Placa</span><span>Marca / Modelo</span><span>Conductor Asignado</span><span>Año</span><span>Dispositivo</span><span>Unidad</span><span>Acción</span>
              </div>
              {vehicles.filter(v =>
                v.plate.toLowerCase().includes(search.toLowerCase()) ||
                v.brand.toLowerCase().includes(search.toLowerCase()) ||
                (v.driverName ?? '').toLowerCase().includes(search.toLowerCase())
              ).map(v => (
                <div key={v.id} className="admin-table__row" id={`admin-vehicle-${v.id}`} onClick={() => setSelectedVehicle(v)} style={{ cursor: 'pointer', gridTemplateColumns: '1fr 1.2fr 1.2fr 80px 1fr 1fr 140px' }}>
                  <span className="admin-table__name" style={{ color: 'var(--accent)', fontWeight: 700 }}>{v.plate}</span>
                  <span>{v.brand} {v.model}</span>
                  <span style={{ fontWeight: 600, color: 'var(--text-primary)' }}>{v.driverName || 'Sin asignar'}</span>
                  <span>{v.year}</span>
                  <span className="admin-table__device">{v.deviceId || 'Sin instalar'}</span>
                  <span className="admin-table__unit">{v.subUnitName}</span>
                  <span className="admin-table__actions" style={{ display: 'flex', gap: '4px' }}>
                    <Button variant="ghost" size="sm" leftIcon={<Eye size={13} />} onClick={(e) => { e.stopPropagation(); setSelectedVehicle(v); }}>
                      Ver
                    </Button>
                    <Button variant="ghost" size="sm" leftIcon={<Edit size={13} />} onClick={(e) => handleOpenEdit(v, e)}>
                      Editar
                    </Button>
                  </span>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Devices tab (FE-14: GET /devices real; no hay alta/edición del
            dispositivo en sí, solo declarar sus cámaras) */}
        {activeTab === 'devices' && (
          <div className="admin-table-container">
            {devicesLoading ? (
              <p style={{ fontSize: '13px', color: 'var(--text-muted)', padding: '1rem' }}>Cargando dispositivos…</p>
            ) : (
              <div className="admin-table" style={{ minWidth: '720px', gridTemplateColumns: '1fr 1fr 1fr 170px' }}>
                <div className="admin-table__header" style={{ gridTemplateColumns: '1fr 1fr 1fr 170px' }}>
                  <span>IMEI</span><span>Modelo</span><span>Vehículo instalado</span><span>Acción</span>
                </div>
                {devicesList.filter(d =>
                  d.imei.toLowerCase().includes(search.toLowerCase())
                ).map(d => {
                  const installedVehicle = vehicles.find(v => v.id === d.installedVehicleId);
                  return (
                    <div key={d.id} className="admin-table__row" id={`admin-device-${d.imei}`} style={{ gridTemplateColumns: '1fr 1fr 1fr 170px' }}>
                      <span className="admin-table__name">{d.imei}</span>
                      <span>{d.model || '—'}</span>
                      <span>{installedVehicle?.plate ?? 'Sin instalar'}</span>
                      <span className="admin-table__actions">
                        <Button variant="ghost" size="sm" leftIcon={<Cable size={13} />} onClick={() => setInstallDevice(d)}>
                          {d.installedVehicleId ? 'Gestionar' : 'Instalar'}
                        </Button>
                        <Button variant="ghost" size="sm" leftIcon={<Video size={13} />} onClick={() => setCamerasDevice(d)}>
                          Cámaras
                        </Button>
                      </span>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        )}

        {/* FE-13 — catálogo de tipos de novedad. Panel autónomo: su alta pide
            código Y etiqueta, y el `AdminFormModal` está cableado a la forma de
            los otros cuatro tabs. */}
        {activeTab === 'logbook-types' && <LogbookTypesPanel search={search} />}
        {activeTab === 'expense-categories' && <ExpenseCategoriesPanel search={search} />}

        {/* Sub-units tab */}
        {activeTab === 'subunits' && (
          <div className="admin-page__subunits">
            {subunitsList.filter(s =>
              s.name.toLowerCase().includes(search.toLowerCase())
            ).map(s => (
              <Card key={s.id} className="admin-page__subunit-card" id={`admin-subunit-${s.id}`}>
                <div className="admin-page__subunit-icon"><Building2 size={24} /></div>
                <div className="admin-page__subunit-info">
                  <h3 className="admin-page__subunit-name">{s.name}</h3>
                  <p className="admin-page__subunit-stats">
                    <Truck size={13} /> {s.vehicles} vehículos &nbsp;·&nbsp;
                    <Users size={13} /> {s.drivers} conductores
                  </p>
                </div>
                <Button variant="ghost" size="sm" leftIcon={<Edit size={13} />} onClick={(e) => handleOpenEdit(s, e)}>
                  Editar
                </Button>
              </Card>
            ))}
          </div>
        )}
      </div>

      {/* Vehicle Detail Modal */}
      <VehicleDetailModal
        isOpen={selectedVehicle !== null}
        onClose={() => setSelectedVehicle(null)}
        vehicle={selectedVehicle}
      />

      {/* Admin Add / Edit Form Modal — el catálogo de novedades NO pasa por
          aquí: este modal sabe pintar las cuatro formas de los otros tabs, y
          ampliar su tipo para aceptar una quinta que no sabe dibujar sería
          hacerle prometer algo que no hace. El panel trae su propio alta. */}
      {activeTab !== 'logbook-types' && activeTab !== 'expense-categories' && (
      <AdminFormModal
        isOpen={isFormOpen}
        onClose={() => setIsFormOpen(false)}
        tab={activeTab}
        initialData={editingItem}
        onSave={handleSaveItem}
        subunitOptions={subunitOptions}
        allDriverOptions={allDriverOptions}
        currentUserRole={user?.role}
      />
      )}

      {/* Cámaras del dispositivo (FE-14) */}
      <DeviceCamerasModal
        isOpen={camerasDevice !== null}
        onClose={() => { setCamerasDevice(null); refetchDevices(); }}
        device={camerasDevice}
      />

      <DeviceInstallModal
        isOpen={installDevice !== null}
        onClose={() => { setInstallDevice(null); refetchDevices(); refetchVehicles(); }}
        device={installDevice}
        vehicles={vehicles}
        canManageCapture={user?.role === 'admin'}
      />
    </div>
  );
}
